import { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Link, Outlet } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Layout, Drawer, Button } from 'antd';
import { MenuOutlined, CloseOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import SiderMenuComponent from '../../components/SiderMenuComponent.jsx';
import HeaderMenuComponent from '../../components/HeaderMenuComponent.jsx';
import HeaderProfileDropdownComponent from '../../components/HeaderProfileDropdownComponent.jsx';
import { ReactComponent as IconLogo } from '../../assets/icons/icon-logo.svg';
import modules from '../navigationRoutes.js';
import SupportModal from '../../components/SupportModal.jsx';
import HelpButton from '../../components/HelpButton.jsx';
import useAccessCheckOnNavigation from '../../hooks/useAccessCheckOnNavigation.js';

const { Sider, Header, Content, Footer } = Layout;

const mobileModules = modules.map(module => {
  if (module.pages.length > 0) {
    return {
      ...module,
      children: module.pages
    };
  } else {
    return module;
  }
});

const MainLayout = () => {
  const { isMobile, currentServer } = useSelector((store) => store.common);
  const { currentModule } = useSelector((store) => store.nav);
  const [pages, setPages] = useState([]);
  const [supportVisible, setSupportVisible] = useState(false);
  const [bannerVisible, setBannerVisible] = useState(false);
  const [showHelpTooltip, setShowHelpTooltip] = useState(false);
  const matchingModule = useMemo(() => modules.find(module => module.key === currentModule) || {}, [currentModule]);
  const [invertColor, setInvertColor] = useState(false);

  // Проверяем доступ к компонентам при навигации
  useAccessCheckOnNavigation();

  const location = useLocation();
  useEffect(() => {
    setInvertColor(location?.pathname === '/documentation/errorlogs' || (currentServer &&currentServer !== 'https://sitiy-api.ru') || false);
  }, [currentServer, location]);

  useEffect(() => {
    setPages(matchingModule?.pages || []);
  }, [matchingModule])

  useEffect(() => {
    const bannerClosed = localStorage.getItem('supportBannerClosed');
    if (!bannerClosed) {
      setBannerVisible(true);
    }
  }, []);

  const openSupport = () => setSupportVisible(true);
  const closeSupport = () => setSupportVisible(false);
  
  const closeBanner = () => {
    setBannerVisible(false);
    localStorage.setItem('supportBannerClosed', 'true');
    setShowHelpTooltip(true);
    setTimeout(() => setShowHelpTooltip(false), 5100);
  };

  return (
    <>
      {bannerVisible && (
        <Banner>
          <BannerContent>
            <button onClick={openSupport} style={{ background: 'none', border: 'none', padding: 0, margin: 0, fontSize: '14px', fontWeight: 'bold', color: '#ffffff', textDecoration: 'underline', cursor: 'pointer' }}>
              Видео-инструкция по работе с личным кабинетом и техническая поддержка
            </button>
            <CloseButton onClick={closeBanner}>
              <CloseOutlined />
            </CloseButton>
          </BannerContent>
        </Banner>
      )}
      {supportVisible && <SupportModal open={supportVisible} onClose={closeSupport} />}
      <StyledLayout>
        {isMobile ? (
          <StyledLayout>
            <StyledHeader $invert={invertColor}>
              <MobileMenu modules={mobileModules} />
              <HeaderProfileDropdownComponent />
            </StyledHeader>
            <StyledContent>
              <Outlet />
            </StyledContent>
            <StyledFooter>
              <Link to="/kitchen/dashboard">
                <IconLogo />
              </Link>
            </StyledFooter>
          </StyledLayout>
        ) : (
          <>
            <StyledSider collapsed $invert={invertColor}>
              <WrapperLogo>
                <Link to="/kitchen/dashboard">
                  <IconLogo />
                </Link>
              </WrapperLogo>
              <SiderMenuComponent modules={modules} $invert={invertColor}/>
            </StyledSider>
            <StyledLayout>
              <StyledHeader $invert={invertColor}>
                <HeaderMenuComponent pages={pages} $invert={invertColor}/>
                <HeaderProfileDropdownComponent />
              </StyledHeader>
              <StyledContent>
                <Outlet />
              </StyledContent>
              <HelpButton showTooltip={showHelpTooltip} />
            </StyledLayout>
          </>
        )}
      </StyledLayout>
    </>
  );
};

export default MainLayout;

const Banner = styled.div`
  /* position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9999; */
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 40px;
  padding: 10px 20px;
  background: rgba(234, 57, 75, 0.8);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  @media (max-width: 748px) {
    min-height: 60px;
    padding: 5px 10px;
  }
`

const BannerContent = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 0 40px;
  position: relative;
  width: 100%;
  padding-right: 40px;
  @media (max-width: 1024px) {
    gap: 0 10px;
  }
`

const CloseButton = styled.button`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  font-size: 18px;
  color: #ffffff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 0.7;
  }

  @media (max-width: 768px) {
    font-size: 16px;
    width: 25px;
    height: 25px;
  }
`
const StyledLayout = styled(Layout)`
  position: relative;
  width: 100%;
  height: 100vh;
  overflow: hidden;
`
const StyledSider = styled(Sider)`
  box-shadow: var(--shadow-contour); 
  /* filter: ${(p) => p.$invert ? 'invert(1)' : 'none'}; */
  background-color: ${(p) => p.$invert ? '#2f2f2f' : '#fff'} !important;
  color: ${(p) => p.$invert ? '#fff' : '#000'} !important;
  transition: filter 0.2s ease;
`
const WrapperLogo = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  /* width: 100%; */
  height: 45px;
  svg {
    width: 60px;
    fill: #EA394B;
  }
`
const StyledHeader = styled(Header)`
  display: flex;
  align-items: center;
  @media screen and (max-width: 768px) {
    padding: 0 5px;
  }
  /* filter: ${(p) => p.$invert ? 'invert(1)' : 'none'}; */
  background-color: ${(p) => p.$invert ? '#2f2f2f' : '#fff'} !important;
  color: ${(p) => p.$invert ? '#fff' : '#000'} !important;
  transition: filter 0.2s ease;
`
const StyledContent = styled(Content)`
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`
const StyledFooter = styled(Footer)`
  position: fixed;
  bottom: ${(props) => props.show ? 0 : '-75px'};
  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: 4px 20px;
  width: 100%;
  background: white;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
  transition: all .5s ease;
  & svg {
    height: 30px;
  }
`

const MobileMenu = ({ modules }) => {
  const [visible, setVisible] = useState(false);

  const showDrawer = () => {
    setVisible(true);
  };

  const onClose = () => {
    setVisible(false);
  };

  return (
    <>
      <Button style={{position: 'absolute', top: '5px', left: '5px', zIndex: '1000', padding: '0 5px', width: 'fit-content', fontSize: '24px', color: '#555555'}} type='text' size='large' onClick={showDrawer}>
        <MenuOutlined />
      </Button>
      <CustomDrawer
        title={
          <>
            <WrapperLogo>
              <IconLogo/>
            </WrapperLogo>
            <Button
              type="text"
              icon={<CloseOutlined style={{fontSize: '25px', color: '#aeaeae'}}/>}
              onClick={onClose}
              style={{ position: 'absolute', top: '20px', right: '20px', zIndex: 1 }}
            />
          </>
        }
        placement="left"
        open={visible}
        closeIcon={false}
        style={{padding: '0 !important'}}
      >
        <SiderMenuComponent modules={modules} onClose={onClose}/>
      </CustomDrawer>
    </>
  );
};

const CustomDrawer = styled(Drawer)`
  .ant-drawer-body {
    padding: 10px;
  }
`;